import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type {
  AggregatedStats,
  Player,
  PlayerCareerStats,
  Position,
  TeamMember,
} from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox, CheckboxField } from '../../ui/checkbox';
import { ErrorMessage, Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Text } from '../../ui/text';
import { Panel } from '../../components/PageChrome';

const POSITIONS: Position[] = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'EH'];

interface FormState {
  firstName: string;
  lastName: string;
  preferredName: string;
  jerseyNumber: string;
  primaryPositions: Position[];
  photoKey: string; // local-only state used by upload; we save photoUrl
  photoUrl: string;
  bats: '' | 'R' | 'L' | 'S';
  throws: '' | 'R' | 'L';
  heightInches: string;
  weightLbs: string;
  birthdate: string;
  graduationYear: string;
  profileVisibility: NonNullable<Player['profileVisibility']>;
  bio: string;
  guardianMemberIds: string[];
  active: boolean;
  address: string;
  contactEmail: string;
  contactPhone: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  medicalNotes: string;
  birthCertificateKey: string;
}

const fromPlayer = (p: Player): FormState => ({
  firstName: p.firstName,
  lastName: p.lastName,
  preferredName: p.preferredName ?? '',
  jerseyNumber: p.jerseyNumber,
  primaryPositions: [...p.primaryPositions],
  photoKey: '',
  photoUrl: p.photoUrl ?? '',
  bats: p.bats ?? '',
  throws: p.throws ?? '',
  heightInches: p.heightInches !== undefined ? String(p.heightInches) : '',
  weightLbs: p.weightLbs !== undefined ? String(p.weightLbs) : '',
  birthdate: p.birthdate ?? p.privateProfile?.birthdate ?? '',
  graduationYear: p.graduationYear ? String(p.graduationYear) : '',
  profileVisibility: p.profileVisibility ?? 'TEAM',
  bio: p.bio ?? '',
  guardianMemberIds: p.guardianMemberIds ?? [],
  active: p.active,
  address: p.privateProfile?.address ?? '',
  contactEmail: p.privateProfile?.contactEmail ?? '',
  contactPhone: p.privateProfile?.contactPhone ?? '',
  emergencyContactName: p.privateProfile?.emergencyContactName ?? '',
  emergencyContactPhone: p.privateProfile?.emergencyContactPhone ?? '',
  medicalNotes: p.privateProfile?.medicalNotes ?? '',
  birthCertificateKey: p.privateProfile?.birthCertificateKey ?? '',
});

const nullable = (value: string) => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const heightDisplay = (inches: number | undefined) => {
  if (inches === undefined) return '—';
  const feet = Math.floor(inches / 12);
  const rem = inches % 12;
  return `${feet}'${rem}"`;
};

const formatDob = (iso: string | undefined) => {
  if (!iso) return '—';
  // Treat as plain calendar date — avoid timezone shifting.
  const [y, m, d] = iso.split('-').map((s) => Number(s));
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface DetailRowProps {
  label: string;
  value?: React.ReactNode;
}
function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <Text className="text-xs uppercase tracking-wide text-zinc-500">{label}</Text>
      <Text className="text-sm text-zinc-100">{value ?? '—'}</Text>
    </div>
  );
}

interface StatsTableProps {
  rows: Array<{ label: string; stats: AggregatedStats }>;
}
function StatsTable({ rows }: StatsTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-zinc-800 text-zinc-400">
            <th className="px-2 py-1.5 font-medium">Span</th>
            <th className="px-2 py-1.5 text-right font-medium">G</th>
            <th className="px-2 py-1.5 text-right font-medium">AB</th>
            <th className="px-2 py-1.5 text-right font-medium">H</th>
            <th className="px-2 py-1.5 text-right font-medium">HR</th>
            <th className="px-2 py-1.5 text-right font-medium">RBI</th>
            <th className="px-2 py-1.5 text-right font-medium">BB</th>
            <th className="px-2 py-1.5 text-right font-medium">SO</th>
            <th className="px-2 py-1.5 text-right font-medium">AVG</th>
            <th className="px-2 py-1.5 text-right font-medium">OBP</th>
            <th className="px-2 py-1.5 text-right font-medium">SLG</th>
            <th className="px-2 py-1.5 text-right font-medium">OPS</th>
            <th className="px-2 py-1.5 text-right font-medium">IP</th>
            <th className="px-2 py-1.5 text-right font-medium">ERA</th>
            <th className="px-2 py-1.5 text-right font-medium">WHIP</th>
            <th className="px-2 py-1.5 text-right font-medium">PO</th>
            <th className="px-2 py-1.5 text-right font-medium">A</th>
            <th className="px-2 py-1.5 text-right font-medium">E</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-zinc-900 last:border-0 text-zinc-200">
              <td className="px-2 py-1.5 font-medium">{row.label}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.games}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.ab}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.h}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.hr}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.rbi}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.bb}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.so}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.avg.toFixed(3)}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.obp.toFixed(3)}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.slg.toFixed(3)}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.batting.ops.toFixed(3)}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.pitching.ip}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.pitching.era.toFixed(2)}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.pitching.whip.toFixed(2)}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.fielding.po}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.fielding.a}</td>
              <td className="px-2 py-1.5 text-right">{row.stats.fielding.e}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PlayerDetails() {
  const { teamId, playerId } = useParams<{ teamId: string; playerId: string }>();
  const [player, setPlayer] = useState<Player>();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [careerStats, setCareerStats] = useState<PlayerCareerStats>();
  const [form, setForm] = useState<FormState>();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<'avatar' | 'birthCert' | null>(null);
  const [error, setError] = useState<string>();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const certInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!teamId || !playerId) return;
    setError(undefined);
    const [p, mres, stats] = await Promise.all([
      admin.getPlayer(teamId, playerId),
      admin.listMembers(teamId).catch(() => ({ members: [] as TeamMember[] })),
      admin
        .getPlayerCareerStats(teamId, playerId)
        .catch(() => undefined as PlayerCareerStats | undefined),
    ]);
    setPlayer(p);
    setMembers(mres.members);
    setCareerStats(stats);
    setForm(fromPlayer(p));
  };

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, playerId]);

  const guardiansById = useMemo(() => {
    const map = new Map<string, TeamMember>();
    for (const m of members) map.set(m.memberId, m);
    return map;
  }, [members]);

  const guardianCandidates = useMemo(
    () =>
      members
        .filter((m) =>
          m.roles.some((r) => r === 'GUARDIAN' || r === 'FAMILY' || r === 'PLAYER'),
        )
        .filter((m) => m.status === 'APPROVED' || m.status === 'INVITED' || m.status === 'PENDING'),
    [members],
  );

  const togglePosition = (pos: Position) => {
    if (!form) return;
    setForm({
      ...form,
      primaryPositions: form.primaryPositions.includes(pos)
        ? form.primaryPositions.filter((p) => p !== pos)
        : [...form.primaryPositions, pos],
    });
  };

  const toggleGuardian = (memberId: string) => {
    if (!form) return;
    setForm({
      ...form,
      guardianMemberIds: form.guardianMemberIds.includes(memberId)
        ? form.guardianMemberIds.filter((id) => id !== memberId)
        : [...form.guardianMemberIds, memberId],
    });
  };

  const uploadAvatar = async (file: File | undefined) => {
    if (!teamId || !playerId || !file || !form) return;
    setUploading('avatar');
    setError(undefined);
    try {
      const signed = await admin.createAssetUploadUrl(teamId, {
        type: 'playerAvatar',
        playerId,
        contentType: file.type,
      });
      const res = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status} ${res.statusText}`);
      setForm({ ...form, photoUrl: signed.publicUrl ?? '', photoKey: signed.key });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(null);
    }
  };

  const uploadBirthCertificate = async (file: File | undefined) => {
    if (!teamId || !playerId || !file || !form) return;
    setUploading('birthCert');
    setError(undefined);
    try {
      const signed = await admin.createAssetUploadUrl(teamId, {
        type: 'playerBirthCertificate',
        playerId,
        contentType: file.type,
      });
      const res = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status} ${res.statusText}`);
      setForm({ ...form, birthCertificateKey: signed.key });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(null);
    }
  };

  const viewBirthCertificate = async () => {
    if (!teamId || !playerId) return;
    setError(undefined);
    try {
      const { url } = await admin.getPlayerBirthCertificateUrl(teamId, playerId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamId || !playerId || !form) return;
    setSaving(true);
    setError(undefined);
    try {
      const heightVal = form.heightInches.trim() === '' ? null : Number(form.heightInches);
      const weightVal = form.weightLbs.trim() === '' ? null : Number(form.weightLbs);
      const gradVal = form.graduationYear.trim() === '' ? null : Number(form.graduationYear);
      await admin.updatePlayer(teamId, playerId, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        preferredName: nullable(form.preferredName),
        jerseyNumber: form.jerseyNumber.trim(),
        primaryPositions: form.primaryPositions,
        photoUrl: nullable(form.photoUrl),
        bats: form.bats || null,
        throws: form.throws || null,
        heightInches: Number.isFinite(heightVal as number) ? (heightVal as number) : heightVal,
        weightLbs: Number.isFinite(weightVal as number) ? (weightVal as number) : weightVal,
        birthdate: nullable(form.birthdate),
        graduationYear: Number.isFinite(gradVal as number) ? (gradVal as number) : gradVal,
        profileVisibility: form.profileVisibility,
        bio: nullable(form.bio),
        guardianMemberIds: form.guardianMemberIds,
        active: form.active,
        privateProfile: {
          birthdate: form.birthdate.trim() || undefined,
          address: form.address.trim() || undefined,
          contactEmail: form.contactEmail.trim() || undefined,
          contactPhone: form.contactPhone.trim() || undefined,
          emergencyContactName: form.emergencyContactName.trim() || undefined,
          emergencyContactPhone: form.emergencyContactPhone.trim() || undefined,
          medicalNotes: form.medicalNotes.trim() || undefined,
          birthCertificateKey: form.birthCertificateKey.trim() || undefined,
        },
      });
      await load();
      setMode('view');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (!player) return;
    setForm(fromPlayer(player));
    setError(undefined);
    setMode('view');
  };

  if (!form || !player) {
    return (
      <div>
        {error ? (
          <Text className="text-red-400">{error}</Text>
        ) : (
          <Text>Loading player…</Text>
        )}
      </div>
    );
  }

  const guardians = (player.guardianMemberIds ?? [])
    .map((id) => guardiansById.get(id))
    .filter((m): m is TeamMember => Boolean(m));

  const header = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex items-center gap-4">
        <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
          {player.photoUrl ? (
            <img src={player.photoUrl} alt="" className="size-full object-cover" />
          ) : (
            <span className="font-mono text-zinc-400">#{player.jerseyNumber || '?'}</span>
          )}
        </div>
        <div>
          <Heading level={2}>{player.name}</Heading>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Badge color="zinc">#{player.jerseyNumber}</Badge>
            <Badge color={player.active ? 'lime' : 'zinc'}>
              {player.active ? 'Active' : 'Inactive'}
            </Badge>
            <Badge color="zinc">{player.profileVisibility ?? 'TEAM'}</Badge>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button href={`/admin/teams/${teamId}`} outline>
          Roster
        </Button>
        {mode === 'view' ? (
          <Button color="dark/zinc" onClick={() => setMode('edit')}>
            Edit
          </Button>
        ) : null}
      </div>
    </div>
  );

  if (mode === 'view') {
    return (
      <div className="space-y-6">
        {header}

        <Panel>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <DetailRow label="Preferred name" value={player.preferredName} />
            <DetailRow
              label="Positions"
              value={
                player.primaryPositions.length > 0
                  ? player.primaryPositions.join(', ')
                  : undefined
              }
            />
            <DetailRow label="Bats" value={player.bats} />
            <DetailRow label="Throws" value={player.throws} />
            <DetailRow label="Height" value={heightDisplay(player.heightInches)} />
            <DetailRow
              label="Weight"
              value={player.weightLbs ? `${player.weightLbs} lbs` : undefined}
            />
            <DetailRow label="Date of birth" value={formatDob(player.birthdate)} />
            <DetailRow label="Graduation year" value={player.graduationYear} />
          </div>
          {player.bio ? (
            <div className="mt-4">
              <Text className="text-xs uppercase tracking-wide text-zinc-500">Bio</Text>
              <Text className="mt-1 whitespace-pre-wrap text-sm text-zinc-200">{player.bio}</Text>
            </div>
          ) : null}
        </Panel>

        <Panel>
          <Heading level={3}>Career stats</Heading>
          {careerStats && (careerStats.career.games > 0 || careerStats.bySeason.length > 0) ? (
            <div className="mt-3 space-y-3">
              <StatsTable rows={[{ label: 'Career', stats: careerStats.career }]} />
              {careerStats.bySeason.length > 0 ? (
                <StatsTable
                  rows={careerStats.bySeason.map((split) => ({
                    label: split.seasonName ?? split.seasonId ?? 'Unattached',
                    stats: split,
                  }))}
                />
              ) : null}
            </div>
          ) : (
            <Text className="mt-2 text-sm">No game stats yet.</Text>
          )}
        </Panel>

        <Panel>
          <Heading level={3}>Guardians & family</Heading>
          {guardians.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {guardians.map((m) => (
                <li
                  key={m.memberId}
                  className="flex flex-wrap items-center gap-2 text-sm text-zinc-200"
                >
                  <Badge color="zinc">{m.roles.join(', ') || 'Member'}</Badge>
                  <span>{m.displayName ?? m.email ?? m.memberId}</span>
                </li>
              ))}
            </ul>
          ) : (
            <Text className="mt-2 text-sm">No guardians linked.</Text>
          )}
        </Panel>

        <Panel>
          <Heading level={3}>Personal info</Heading>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <DetailRow label="Address" value={player.privateProfile?.address} />
            <DetailRow label="Contact email" value={player.privateProfile?.contactEmail} />
            <DetailRow label="Contact phone" value={player.privateProfile?.contactPhone} />
            <DetailRow
              label="Emergency contact"
              value={
                player.privateProfile?.emergencyContactName
                  ? `${player.privateProfile.emergencyContactName}${
                      player.privateProfile.emergencyContactPhone
                        ? ` · ${player.privateProfile.emergencyContactPhone}`
                        : ''
                    }`
                  : undefined
              }
            />
          </div>
          {player.privateProfile?.medicalNotes ? (
            <div className="mt-4">
              <Text className="text-xs uppercase tracking-wide text-zinc-500">Medical notes</Text>
              <Text className="mt-1 whitespace-pre-wrap text-sm text-zinc-200">
                {player.privateProfile.medicalNotes}
              </Text>
            </div>
          ) : null}
        </Panel>

        <Panel>
          <Heading level={3}>Documents</Heading>
          <div className="mt-3">
            {player.privateProfile?.birthCertificateKey ? (
              <div className="flex flex-wrap items-center gap-3">
                <Badge color="emerald">Birth certificate on file</Badge>
                <Button outline onClick={() => void viewBirthCertificate()}>
                  View
                </Button>
              </div>
            ) : (
              <Text className="text-sm">
                No birth certificate uploaded. Tournaments may require one for age verification.
              </Text>
            )}
          </div>
        </Panel>

        {error ? <ErrorMessage>{error}</ErrorMessage> : null}
      </div>
    );
  }

  // Edit mode
  return (
    <div className="space-y-6">
      {header}

      <form onSubmit={save} className="space-y-6">
        <Panel className="space-y-4">
          <Heading level={3}>Identity</Heading>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
              {form.photoUrl ? (
                <img src={form.photoUrl} alt="" className="size-full object-cover" />
              ) : (
                <span className="font-mono text-zinc-400">#{form.jerseyNumber || '?'}</span>
              )}
            </div>
            <div className="flex flex-1 flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  outline
                  disabled={uploading !== null}
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {uploading === 'avatar' ? 'Uploading…' : 'Upload avatar'}
                </Button>
                {form.photoUrl ? (
                  <Button
                    type="button"
                    plain
                    onClick={() => setForm({ ...form, photoUrl: '', photoKey: '' })}
                  >
                    Remove
                  </Button>
                ) : null}
                <Text className="text-xs text-zinc-500">PNG, JPG, or WebP</Text>
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                aria-label="Upload player avatar"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                onChange={(e) => void uploadAvatar(e.target.files?.[0])}
                disabled={uploading !== null}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field>
                  <Label>First name</Label>
                  <Input
                    required
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  />
                </Field>
                <Field>
                  <Label>Last name</Label>
                  <Input
                    required
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  />
                </Field>
                <Field>
                  <Label>Preferred name</Label>
                  <Input
                    value={form.preferredName}
                    onChange={(e) => setForm({ ...form, preferredName: e.target.value })}
                  />
                </Field>
                <Field>
                  <Label>Jersey #</Label>
                  <Input
                    required
                    pattern="\d{1,3}"
                    maxLength={3}
                    value={form.jerseyNumber}
                    onChange={(e) =>
                      setForm({ ...form, jerseyNumber: e.target.value.replace(/\D/g, '') })
                    }
                  />
                </Field>
                <Field>
                  <Label>Date of birth</Label>
                  <Input
                    type="date"
                    value={form.birthdate}
                    onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
                  />
                </Field>
                <Field>
                  <Label>Graduation year</Label>
                  <Input
                    inputMode="numeric"
                    value={form.graduationYear}
                    onChange={(e) =>
                      setForm({ ...form, graduationYear: e.target.value.replace(/\D/g, '') })
                    }
                  />
                </Field>
              </div>
            </div>
          </div>
        </Panel>

        <Panel className="space-y-4">
          <Heading level={3}>Baseball</Heading>
          <div className="flex flex-wrap gap-1.5">
            {POSITIONS.map((pos) => (
              <button
                key={pos}
                type="button"
                onClick={() => togglePosition(pos)}
                className={
                  form.primaryPositions.includes(pos)
                    ? 'rounded bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-900'
                    : 'rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-400 hover:text-zinc-100'
                }
              >
                {pos}
              </button>
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-4">
            <Field>
              <Label>Bats</Label>
              <Select
                value={form.bats}
                onChange={(e) => setForm({ ...form, bats: e.target.value as FormState['bats'] })}
              >
                <option value="">—</option>
                <option value="R">Right</option>
                <option value="L">Left</option>
                <option value="S">Switch</option>
              </Select>
            </Field>
            <Field>
              <Label>Throws</Label>
              <Select
                value={form.throws}
                onChange={(e) =>
                  setForm({ ...form, throws: e.target.value as FormState['throws'] })
                }
              >
                <option value="">—</option>
                <option value="R">Right</option>
                <option value="L">Left</option>
              </Select>
            </Field>
            <Field>
              <Label>Height (inches)</Label>
              <Input
                inputMode="numeric"
                value={form.heightInches}
                onChange={(e) =>
                  setForm({ ...form, heightInches: e.target.value.replace(/\D/g, '') })
                }
              />
            </Field>
            <Field>
              <Label>Weight (lbs)</Label>
              <Input
                inputMode="numeric"
                value={form.weightLbs}
                onChange={(e) =>
                  setForm({ ...form, weightLbs: e.target.value.replace(/\D/g, '') })
                }
              />
            </Field>
          </div>
          <Field>
            <Label>Bio</Label>
            <Textarea
              rows={3}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
            />
          </Field>
        </Panel>

        <Panel className="space-y-4">
          <Heading level={3}>Guardians & family</Heading>
          {guardianCandidates.length === 0 ? (
            <Text className="text-sm">
              No team members with GUARDIAN, FAMILY, or PLAYER roles yet. Invite them from the
              Members tab first.
            </Text>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {guardianCandidates.map((m) => (
                <CheckboxField key={m.memberId}>
                  <Checkbox
                    checked={form.guardianMemberIds.includes(m.memberId)}
                    onChange={() => toggleGuardian(m.memberId)}
                  />
                  <Label>
                    {m.displayName ?? m.email ?? m.memberId}{' '}
                    <span className="text-xs text-zinc-500">{m.roles.join(', ')}</span>
                  </Label>
                </CheckboxField>
              ))}
            </div>
          )}
        </Panel>

        <Panel className="space-y-4">
          <Heading level={3}>Personal info</Heading>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <Label>Visibility</Label>
              <Select
                value={form.profileVisibility}
                onChange={(e) =>
                  setForm({
                    ...form,
                    profileVisibility: e.target.value as FormState['profileVisibility'],
                  })
                }
              >
                <option value="PRIVATE">Private</option>
                <option value="TEAM">Team</option>
                <option value="PUBLIC">Public</option>
              </Select>
            </Field>
            <Field className="sm:col-span-1">
              <Label>Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </Field>
            <Field>
              <Label>Contact email</Label>
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              />
            </Field>
            <Field>
              <Label>Contact phone</Label>
              <Input
                value={form.contactPhone}
                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
              />
            </Field>
            <Field>
              <Label>Emergency contact</Label>
              <Input
                value={form.emergencyContactName}
                onChange={(e) => setForm({ ...form, emergencyContactName: e.target.value })}
              />
            </Field>
            <Field>
              <Label>Emergency phone</Label>
              <Input
                value={form.emergencyContactPhone}
                onChange={(e) => setForm({ ...form, emergencyContactPhone: e.target.value })}
              />
            </Field>
          </div>
          <Field>
            <Label>Medical notes</Label>
            <Textarea
              rows={3}
              value={form.medicalNotes}
              onChange={(e) => setForm({ ...form, medicalNotes: e.target.value })}
            />
          </Field>
          <CheckboxField>
            <Checkbox
              checked={form.active}
              onChange={(checked) => setForm({ ...form, active: checked })}
            />
            <Label>Player is active</Label>
          </CheckboxField>
        </Panel>

        <Panel className="space-y-3">
          <Heading level={3}>Documents</Heading>
          <Text className="text-sm">
            Birth certificate. Tournaments often require it for age verification. Visible to staff
            and linked guardians only.
          </Text>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              outline
              disabled={uploading !== null}
              onClick={() => certInputRef.current?.click()}
            >
              {uploading === 'birthCert'
                ? 'Uploading…'
                : form.birthCertificateKey
                  ? 'Replace birth certificate'
                  : 'Upload birth certificate'}
            </Button>
            {form.birthCertificateKey ? (
              <>
                <Button type="button" plain onClick={() => void viewBirthCertificate()}>
                  View current
                </Button>
                <Button
                  type="button"
                  plain
                  onClick={() => setForm({ ...form, birthCertificateKey: '' })}
                  className="text-red-400"
                >
                  Remove
                </Button>
              </>
            ) : null}
            <Text className="text-xs text-zinc-500">PDF, PNG, JPG, or WebP</Text>
          </div>
          <input
            ref={certInputRef}
            type="file"
            aria-label="Upload birth certificate"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            className="sr-only"
            onChange={(e) => void uploadBirthCertificate(e.target.files?.[0])}
            disabled={uploading !== null}
          />
        </Panel>

        {error ? <ErrorMessage>{error}</ErrorMessage> : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" color="dark/zinc" disabled={saving || uploading !== null}>
            {saving ? 'Saving…' : 'Save player'}
          </Button>
          <Button type="button" outline onClick={cancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
